if (!customElements.get("card-product")) {
  customElements.define(
    "card-product",
    class CardProduct extends HTMLElement {
      constructor() {
        super();
      }

      // Setup lives in connectedCallback, not the constructor. A swapped-in
      // card (see setupSwatches/swapToProduct below) is built by parsing a
      // fetched HTML string with DOMParser, at which point `card-product` is
      // ALREADY a registered custom element (customElements.define ran back
      // at page load) — so the parser constructs it the instant it sees the
      // opening tag, before the form/swatches/size-buttons inside it have
      // been parsed and appended as children. Querying for them in the
      // constructor would find nothing and bail out silently, leaving the
      // swapped card's swatches as inert, unhandled <a> tags (clicking one
      // just navigates like a normal link instead of swapping again).
      // connectedCallback only fires once the element is actually inserted
      // into the live document, by which point its full subtree already
      // exists (it was built inside the detached parser document before
      // replaceWith moved it over) — same reasoning as the reviews-carousel
      // custom-element timing gotcha, see shopify_lillypilly_reviews_image_section memory.
      connectedCallback() {
        if (this.initialized) return;

        this.form = this.querySelector('form[data-type="add-to-cart-form"]');
        if (!this.form) return;

        this.sizeButtons = this.querySelectorAll(".size-option");
        this.swatchLinks = this.querySelectorAll(".swatch-item");

        // Add hidden add to cart button
        this.addToCartButton = document.createElement("button");
        this.addToCartButton.type = "submit";
        this.addToCartButton.name = "add";
        this.addToCartButton.className = "hidden-add-to-cart";
        this.addToCartButton.style.display = "none";
        this.form.appendChild(this.addToCartButton);

        this.setupSizeButtons();
        this.setupFormSubmit();
        this.setupSwatches();

        this.initialized = true;
      }

      // Colour swatches are separate products on this store (see
      // shopify_lillypilly_colour_swatches memory), not variants. Clicking a
      // non-selected swatch fetches that product's card fully server-rendered
      // (via the hidden sections/card-swatch-render.liquid proxy + Shopify's
      // Section Rendering API) and swaps it in place, instead of duplicating
      // price/sale-badge/size-availability logic in JS. Replacing the whole
      // <card-product> element (not just its innerHTML) matters here: custom
      // elements only construct/upgrade when newly connected to the DOM, so
      // an innerHTML swap alone would leave the new size buttons/form with no
      // click handlers wired up.
      setupSwatches() {
        if (!this.swatchLinks.length) return;
        this.swatchLinks.forEach((link) => {
          link.addEventListener("click", (event) => {
            event.preventDefault();
            if (link.classList.contains("swatch-item--selected")) return;
            this.swapToProduct(link.getAttribute("href"));
          });
        });
      }

      swapToProduct(url) {
        if (!url) return;
        this.classList.add("is-swapping");
        const separator = url.indexOf("?") === -1 ? "?" : "&";
        // Matching Sets (14 Oct 2026): swapped colours keep the "Pairs with / Add the set" text via the sets proxy section
        const lpSets = /\/collections\/matching-sets(\/|$)/.test(window.location.pathname);
        fetch(url + separator + "section_id=" + (lpSets ? "card-swatch-render-sets" : "card-swatch-render"))
          .then((response) => {
            if (!response.ok) throw new Error("Swatch card fetch failed");
            return response.text();
          })
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, "text/html");
            const newCard = doc.querySelector("card-product");
            if (!newCard) throw new Error("No card-product in swatch response");
            this.replaceWith(newCard);
          })
          .catch(() => {
            // Progressive enhancement: never leave the shopper stuck on a
            // dimmed card if the fetch fails — fall back to a normal
            // navigation to the sibling colour's own product page.
            window.location.href = url;
          });
      }

      setupSizeButtons() {
        this.sizeButtons.forEach((button) => {
          button.addEventListener("click", () => {
            const variantId = button.getAttribute("data-variant-id");
            const hiddenInput = this.form.querySelector('input[name="id"]');
            if (!hiddenInput) return;
            hiddenInput.value = variantId;
            // Trigger the hidden add to cart button after a short delay
            setTimeout(() => {
              this.addToCartButton.click();
            }, 100);
          });
        });
      }

      setupFormSubmit() {
        this.form.addEventListener("submit", (e) => {
          e.preventDefault();

          const formData = new FormData(this.form);

          // Get the cart drawer
          const cart = document.querySelector("cart-drawer");
          if (cart) {
            // Add sections to update
            formData.append(
              "sections",
              cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append("sections_url", window.location.pathname);
            // Set active element for focus management
            cart.setActiveElement(document.activeElement);
          }

          fetch(window.Shopify.routes.root + "cart/add.js", {
            method: "POST",
            body: formData,
          })
            .then((response) => response.json())
            .then((response) => {
              if (response.status) {
                // Handle error case
                console.error("Error adding to cart:", response.description);
                return;
              }

              // Try to find the cart drawer
              const cart = document.querySelector("cart-drawer");
              if (cart) {
                // Remove is-empty class if present
                cart.classList.remove("is-empty");
                // Render the updated cart contents
                cart.renderContents(response);
              } else {
                // If no cart drawer, refresh the page to show updated cart
                window.location.reload();
              }
            })
            .catch((error) => {
              console.error("Error:", error);
            });
        });
      }
    }
  );
}


// Matching Sets "Add the set" (adam, 14 Oct 2026): one tap adds both pieces of the set in that size, then opens the cart drawer.
document.addEventListener('click', (event) => {
  const btn = event.target.closest('[data-lp-set-ids]');
  if (!btn) return;
  event.preventDefault();
  const ids = btn.getAttribute('data-lp-set-ids').split(',');
  const cart = document.querySelector('cart-drawer');
  const body = { items: ids.map((id) => ({ id: Number(id), quantity: 1 })) };
  if (cart && cart.getSectionsToRender) { body.sections = cart.getSectionsToRender().map((s) => s.id); body.sections_url = window.location.pathname; }
  btn.classList.add('is-adding');
  fetch(window.Shopify.routes.root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) })
    .then((r) => r.json())
    .then((res) => {
      btn.classList.remove('is-adding');
      if (res.status) { console.error('Add the set:', res.description); return; }
      const wrap = btn.closest('.lp-set__add'); const label = wrap && wrap.querySelector('.lp-set__label');
      if (label) { const old = label.textContent; label.textContent = 'Set added ✓'; setTimeout(() => (label.textContent = old), 2500); }
      if (cart && cart.renderContents) { cart.classList.remove('is-empty'); cart.renderContents(res); }
      else window.location.href = window.Shopify.routes.root + 'cart';
    })
    .catch(() => btn.classList.remove('is-adding'));
});
